import type { HttpFetch } from './oauth';

/**
 * Cloud storage destinations (Premium): mirror project files into the customer's own AWS S3
 * bucket or Azure Blob container. Pure HTTP clients (fetch + SubtleCrypto, so they run in Workers
 * and Node) plus the rules for naming files at the destination. No SDKs.
 *
 *  - AWS S3 (and S3-compatible endpoints): Signature Version 4 with an access key that only needs
 *    s3:PutObject and s3:DeleteObject on the bucket/prefix.
 *  - Azure Blob Storage: a container SAS token with create + write (+ delete for the connection
 *    test). Account keys are deliberately not accepted: a SAS is scoped and revocable.
 */

export const CLOUD_STORAGE_PROVIDERS = ['aws_s3', 'azure_blob'] as const;
export type CloudStorageProvider = (typeof CLOUD_STORAGE_PROVIDERS)[number];

/** Domain event the API emits for "Sync now" (payload: { connectionId }); the workers mirror on it. */
export const CLOUD_STORAGE_SYNC_EVENT = 'cloud_storage.sync_requested';

export const CLOUD_STORAGE_LABELS: Record<CloudStorageProvider, string> = {
  aws_s3: 'Amazon S3',
  azure_blob: 'Azure Blob Storage',
};

/** Files above this are recorded as skipped: uploads are single requests buffered in a 128 MB Worker (no multipart yet). */
export const CLOUD_STORAGE_MAX_BYTES = 50 * 1024 * 1024;

export type S3Destination = {
  provider: 'aws_s3';
  bucket: string;
  region: string;
  /** Folder inside the bucket, '' or ending in '/'. */
  prefix: string;
  /** S3-compatible endpoint (https), path-style. Null for AWS. */
  endpoint: string | null;
};
export type S3Secret = { accessKeyId: string; secretAccessKey: string };

export type AzureDestination = {
  provider: 'azure_blob';
  /** https://<account>.blob.core.windows.net/<container> */
  containerUrl: string;
  prefix: string;
};
export type AzureSecret = { sasToken: string };

export type CloudDestination = S3Destination | AzureDestination;
export type CloudSecret = S3Secret | AzureSecret;

export class CloudStorageError extends Error {
  constructor(
    message: string,
    readonly status: number | null = null,
    /** Worth retrying later (throttling, 5xx, network). */
    readonly retryable = false,
  ) {
    super(message);
    this.name = 'CloudStorageError';
  }
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

const AZURE_HOST = /^[a-z0-9]{3,24}\.blob\.core\.(windows\.net|usgovcloudapi\.net|chinacloudapi\.cn)$/;
const BUCKET = /^[a-z0-9][a-z0-9.-]{1,61}[a-z0-9]$/;
const REGION = /^[a-z]{2}(-gov)?-[a-z]+-\d$/;

/** Normalise a user-entered folder: no leading slash, single trailing slash, no `..`. */
export function normalizePrefix(prefix: string | null | undefined): string {
  const parts = (prefix ?? '')
    .split('/')
    .map((p) => p.trim())
    .filter((p) => p && p !== '.' && p !== '..');
  return parts.length ? `${parts.join('/')}/` : '';
}

function isPrivateHost(host: string): boolean {
  return host === 'localhost' || host.endsWith('.localhost') || host.endsWith('.internal') || /^[\d.]+$/.test(host) || host.includes(':') || host.startsWith('[');
}

/** Throws a CloudStorageError with a user-facing message when the destination is unusable. */
export function validateDestination(d: CloudDestination): CloudDestination {
  if (d.provider === 'aws_s3') {
    if (!BUCKET.test(d.bucket) || d.bucket.includes('..')) throw new CloudStorageError('Bucket names are 3-63 lowercase letters, numbers, dots and hyphens');
    if (!d.endpoint && !REGION.test(d.region)) throw new CloudStorageError('Region looks wrong (for example us-east-1)');
    if (d.endpoint) {
      let url: URL;
      try {
        url = new URL(d.endpoint);
      } catch {
        throw new CloudStorageError('Endpoint must be a URL');
      }
      if (url.protocol !== 'https:' || isPrivateHost(url.hostname)) throw new CloudStorageError('Endpoint must be a public https URL');
      return { ...d, endpoint: url.origin, prefix: normalizePrefix(d.prefix) };
    }
    return { ...d, prefix: normalizePrefix(d.prefix) };
  }
  let url: URL;
  try {
    url = new URL(d.containerUrl);
  } catch {
    throw new CloudStorageError('Container URL must look like https://<account>.blob.core.windows.net/<container>');
  }
  const container = url.pathname.split('/').filter(Boolean);
  if (url.protocol !== 'https:' || !AZURE_HOST.test(url.hostname) || container.length !== 1 || !/^[a-z0-9](?:[a-z0-9-]{1,61}[a-z0-9])$/.test(container[0]!)) {
    throw new CloudStorageError('Container URL must look like https://<account>.blob.core.windows.net/<container>');
  }
  return { ...d, containerUrl: `${url.origin}/${container[0]}`, prefix: normalizePrefix(d.prefix) };
}

/** Accepts a SAS token or a full SAS URL; returns the query string without '?'. */
export function normalizeSasToken(value: string): string {
  const v = value.trim();
  const query = v.includes('?') ? v.slice(v.indexOf('?') + 1) : v;
  const params = new URLSearchParams(query);
  if (!params.get('sig') || !params.get('sv')) throw new CloudStorageError('That is not a SAS token (it needs sv= and sig=)');
  return params.toString();
}

/** Human summary of where files go, e.g. "s3://acme-files/buildbee/". */
export function describeDestination(d: CloudDestination): string {
  if (d.provider === 'aws_s3') return `s3://${d.bucket}/${d.prefix}`;
  const url = new URL(d.containerUrl);
  return `${url.hostname.split('.')[0]}/${url.pathname.slice(1)}/${d.prefix}`;
}

// ---------------------------------------------------------------------------
// Crypto helpers
// ---------------------------------------------------------------------------

const enc = new TextEncoder();
/** UTF-8 bytes backed by a plain ArrayBuffer (what SubtleCrypto's types want in every runtime). */
const utf8 = (s: string): Uint8Array<ArrayBuffer> => new Uint8Array(enc.encode(s));
const toHex = (buf: ArrayBuffer) => [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');

export async function sha256HexOf(data: ArrayBuffer | Uint8Array | string): Promise<string> {
  const bytes: Uint8Array<ArrayBuffer> = typeof data === 'string' ? utf8(data) : new Uint8Array(data as ArrayBuffer);
  return toHex(await crypto.subtle.digest('SHA-256', bytes));
}

async function hmac(key: ArrayBuffer | Uint8Array<ArrayBuffer>, message: string): Promise<ArrayBuffer> {
  const k = await crypto.subtle.importKey('raw', key, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  return crypto.subtle.sign('HMAC', k, utf8(message));
}

/** RFC 3986 encoding as S3 expects (slashes kept when `keepSlash`). */
export function awsUriEncode(value: string, keepSlash: boolean): string {
  return [...enc.encode(value)]
    .map((b) => {
      const c = String.fromCharCode(b);
      if (/[A-Za-z0-9\-._~]/.test(c) || (keepSlash && c === '/')) return c;
      return `%${b.toString(16).toUpperCase().padStart(2, '0')}`;
    })
    .join('');
}

export type SigV4Input = {
  method: string;
  url: URL;
  /** Headers to sign (lowercase names). `host` and `x-amz-date` are added. */
  headers: Record<string, string>;
  payloadHash: string;
  region: string;
  service: string;
  accessKeyId: string;
  secretAccessKey: string;
  date: Date;
};

/** AWS Signature Version 4. Returns all headers to send, including Authorization. */
export async function signAwsV4(i: SigV4Input): Promise<Record<string, string>> {
  const amzDate = i.date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const day = amzDate.slice(0, 8);
  const headers: Record<string, string> = { ...i.headers, host: i.url.host, 'x-amz-date': amzDate, 'x-amz-content-sha256': i.payloadHash };
  const names = Object.keys(headers)
    .map((h) => h.toLowerCase())
    .sort();
  const lower = Object.fromEntries(Object.entries(headers).map(([k, v]) => [k.toLowerCase(), v]));
  const canonicalHeaders = names.map((n) => `${n}:${lower[n]!.trim().replace(/\s+/g, ' ')}\n`).join('');
  const signedHeaders = names.join(';');
  const pairs: [string, string][] = [];
  i.url.searchParams.forEach((v, k) => pairs.push([k, v]));
  const query = pairs
    .map(([k, v]) => [awsUriEncode(k, false), awsUriEncode(v, false)] as const)
    .sort(([a, av], [b, bv]) => (a === b ? (av < bv ? -1 : 1) : a < b ? -1 : 1))
    .map(([k, v]) => `${k}=${v}`)
    .join('&');
  const path = awsUriEncode(decodeURIComponent(i.url.pathname), true);
  const canonicalRequest = [i.method, path, query, canonicalHeaders, signedHeaders, i.payloadHash].join('\n');
  const scope = `${day}/${i.region}/${i.service}/aws4_request`;
  const stringToSign = ['AWS4-HMAC-SHA256', amzDate, scope, await sha256HexOf(canonicalRequest)].join('\n');
  let key = await hmac(utf8(`AWS4${i.secretAccessKey}`), day);
  key = await hmac(key, i.region);
  key = await hmac(key, i.service);
  key = await hmac(key, 'aws4_request');
  const signature = toHex(await hmac(key, stringToSign));
  return {
    ...headers,
    authorization: `AWS4-HMAC-SHA256 Credential=${i.accessKeyId}/${scope}, SignedHeaders=${signedHeaders}, Signature=${signature}`,
  };
}

// ---------------------------------------------------------------------------
// Clients
// ---------------------------------------------------------------------------

export interface CloudStorageClient {
  /** Upload (create or overwrite) one object. `key` is relative to the bucket/container. */
  put(key: string, body: ArrayBuffer, contentType: string): Promise<void>;
  delete(key: string): Promise<void>;
}

async function failure(res: Response, provider: CloudStorageProvider): Promise<CloudStorageError> {
  const text = (await res.text().catch(() => '')).slice(0, 2000);
  const code = /<Code>([^<]+)<\/Code>/.exec(text)?.[1] ?? null;
  const label = CLOUD_STORAGE_LABELS[provider];
  const hint =
    res.status === 403
      ? provider === 'aws_s3'
        ? 'access denied: check the access key and that it allows s3:PutObject on this bucket'
        : 'access denied: check that the SAS token is valid, not expired, and allows create + write'
      : res.status === 404
        ? provider === 'aws_s3'
          ? 'bucket not found: check the bucket name and region'
          : 'container not found: check the container URL'
        : res.status === 301 || code === 'PermanentRedirect' || code === 'AuthorizationHeaderMalformed'
          ? 'the bucket is in a different region'
          : `HTTP ${res.status}`;
  return new CloudStorageError(`${label}: ${hint}${code ? ` (${code})` : ''}`, res.status, res.status === 429 || res.status === 503 || res.status >= 500);
}

function s3Url(d: S3Destination, key: string): URL {
  const path = key.split('/').map((p) => awsUriEncode(p, false)).join('/');
  if (d.endpoint) return new URL(`${d.endpoint}/${d.bucket}/${path}`);
  // Bucket names with dots break virtual-hosted TLS; use path style for them.
  if (d.bucket.includes('.')) return new URL(`https://s3.${d.region}.amazonaws.com/${d.bucket}/${path}`);
  return new URL(`https://${d.bucket}.s3.${d.region}.amazonaws.com/${path}`);
}

export function s3Client(d: S3Destination, secret: S3Secret, fetchImpl: HttpFetch, now: () => Date = () => new Date()): CloudStorageClient {
  const send = async (method: 'PUT' | 'DELETE', key: string, body: ArrayBuffer | null, contentType?: string) => {
    const url = s3Url(d, key);
    const payloadHash = await sha256HexOf(body ?? new ArrayBuffer(0));
    const headers = await signAwsV4({
      method,
      url,
      headers: contentType ? { 'content-type': contentType } : {},
      payloadHash,
      region: d.endpoint ? d.region || 'us-east-1' : d.region,
      service: 's3',
      accessKeyId: secret.accessKeyId,
      secretAccessKey: secret.secretAccessKey,
      date: now(),
    });
    delete headers.host;
    let res: Response;
    try {
      res = await fetchImpl(url.toString(), { method, headers, body });
    } catch (err) {
      throw new CloudStorageError(`Amazon S3: network error (${(err as Error).message})`, null, true);
    }
    if (!res.ok && !(method === 'DELETE' && res.status === 404)) throw await failure(res, 'aws_s3');
    await res.body?.cancel().catch(() => undefined);
  };
  return {
    put: (key, body, contentType) => send('PUT', key, body, contentType),
    delete: (key) => send('DELETE', key, null),
  };
}

const AZURE_VERSION = '2023-11-03';

export function azureBlobClient(d: AzureDestination, secret: AzureSecret, fetchImpl: HttpFetch): CloudStorageClient {
  const urlFor = (key: string) => `${d.containerUrl}/${key.split('/').map(encodeURIComponent).join('/')}?${secret.sasToken}`;
  const send = async (method: 'PUT' | 'DELETE', key: string, body: ArrayBuffer | null, contentType?: string) => {
    const headers: Record<string, string> = { 'x-ms-version': AZURE_VERSION, 'x-ms-date': new Date().toUTCString() };
    if (method === 'PUT') {
      headers['x-ms-blob-type'] = 'BlockBlob';
      if (contentType) headers['x-ms-blob-content-type'] = contentType;
    }
    let res: Response;
    try {
      res = await fetchImpl(urlFor(key), { method, headers, body });
    } catch (err) {
      throw new CloudStorageError(`Azure Blob Storage: network error (${(err as Error).message})`, null, true);
    }
    if (!res.ok && !(method === 'DELETE' && res.status === 404)) throw await failure(res, 'azure_blob');
    await res.body?.cancel().catch(() => undefined);
  };
  return {
    put: (key, body, contentType) => send('PUT', key, body, contentType),
    delete: (key) => send('DELETE', key, null),
  };
}

export function cloudStorageClient(d: CloudDestination, secret: CloudSecret, fetchImpl: HttpFetch): CloudStorageClient {
  return d.provider === 'aws_s3' ? s3Client(d, secret as S3Secret, fetchImpl) : azureBlobClient(d, secret as AzureSecret, fetchImpl);
}

/** Write and delete a small marker file; throws CloudStorageError with a useful message on failure. */
export async function testCloudStorage(client: CloudStorageClient, d: CloudDestination): Promise<void> {
  const key = `${d.prefix}.buildbee-connection-test.txt`;
  await client.put(key, utf8(`BuildBee connection test ${new Date().toISOString()}\n`).buffer, 'text/plain');
  // Deleting is best effort: a write-only key/SAS is acceptable.
  await client.delete(key).catch(() => undefined);
}

// ---------------------------------------------------------------------------
// Destination layout
// ---------------------------------------------------------------------------

/** One file at the destination. Folders follow the project: "<prefix><1001 Riverside Tower>/Drawings/...". */
export type MirrorSource =
  | { kind: 'document'; id: string; r2Key: string; project: { number: string; name: string }; folder: string; name: string }
  | { kind: 'drawing'; id: string; r2Key: string; project: { number: string; name: string }; sheet: string; title: string; revision: string }
  | { kind: 'photo'; id: string; r2Key: string; project: { number: string; name: string }; caption: string | null; takenAt: Date | null; createdAt: Date };

/** Make one path segment safe on S3, Azure and the file systems people sync them to. */
export function safeSegment(value: string, max = 120): string {
  const s = value
    .normalize('NFKC')
    // Control characters (char codes < 32) without a regex, then characters unsafe in keys or file names.
    .replace(/./gsu, (ch) => (ch.charCodeAt(0) < 32 ? ' ' : ch))
    .replace(/[\\/:*?"<>|#%{}^~[\]`]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\.+/, '')
    .slice(0, max)
    .trim();
  return s || 'untitled';
}

/** Extension from the stored key (`<ws>/<purpose>/<uuid>-name.pdf` -> `.pdf`). */
export function extensionOf(r2Key: string): string {
  const last = r2Key.split('/').pop() ?? '';
  const m = /\.([A-Za-z0-9]{1,8})$/.exec(last);
  return m ? `.${m[1]!.toLowerCase()}` : '';
}

function withExt(base: string, ext: string): string {
  const b = safeSegment(base);
  return ext && b.toLowerCase().endsWith(ext) ? b : `${b}${ext}`;
}

/** Destination key for a source file (relative to bucket/container, includes the prefix). */
export function mirrorKey(prefix: string, s: MirrorSource): string {
  const project = safeSegment(`${s.project.number} ${s.project.name}`);
  const ext = extensionOf(s.r2Key);
  switch (s.kind) {
    case 'document':
      return `${prefix}${project}/Documents/${safeSegment(s.folder.replace(/_/g, ' '))}/${withExt(s.name.replace(/\.[A-Za-z0-9]{1,8}$/, ''), ext)}`;
    case 'drawing':
      return `${prefix}${project}/Drawings/${withExt(`${s.sheet} ${s.title} (rev ${s.revision})`, ext)}`;
    case 'photo': {
      const when = (s.takenAt ?? s.createdAt).toISOString().slice(0, 10);
      const label = s.caption ? `${safeSegment(s.caption, 60)} ` : '';
      return `${prefix}${project}/Photos/${when}/${withExt(`${label}${s.id.slice(-8)}`, ext)}`;
    }
  }
}

/** Disambiguate two different sources that map to the same destination key. */
export function disambiguateKey(key: string, sourceId: string): string {
  const slash = key.lastIndexOf('/');
  const file = key.slice(slash + 1);
  const dot = file.lastIndexOf('.');
  const suffix = ` (${sourceId.slice(-8)})`;
  return dot > 0 ? `${key.slice(0, slash + 1)}${file.slice(0, dot)}${suffix}${file.slice(dot)}` : `${key}${suffix}`;
}
